import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Button,
  Spinner,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  InputGroup,Alert
} from "react-bootstrap";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaPlus,
  FaSearch,
  FaFilter,
  FaSync,
  FaEye,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import Header from "./Header";
import { getCachedData, setCachedData } from "../utils/apiCache";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { ProductContext } from "../context/AppContext";
import ImageWithBasePath from "../core/common/imageWithBasePath"; // Assuming this is available as in previous
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";


const StudentLeave = () => {
  const navigate = useNavigate();
  const {
    getRegistration,
    allClass = [],
    session = [],
    StudentProfileData,
    GetstudentData,
  } = useContext(ProductContext);

  const userId = GetstudentData?.id;
  const sessionId = GetstudentData?.session;
  const classSectionId = GetstudentData?.class_section_id;
  const [getLeaves, setGetLeaves] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showSlowMsg, message } = useNetworkStatus(loading, 4000);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReApplyModal, setShowReApplyModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const authtoken = localStorage.getItem("token");
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [filteredData, setFilteredData] = useState([]);
  const [holidayCalendar, setHolidayCalendar] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [calendarTooltipRefreshKey, setCalendarTooltipRefreshKey] = useState(0);
  const [schoolTime, setSchoolTime] = useState(null);

  const fetchMyLeavesData = async () => {
    if (!userId) return;
    const cacheKey = `student_leaves_data_${userId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setGetLeaves({ leaveData: cached });
      setFilteredData(cached);
      setLoading(false);
    }
    try {
      if (!cached) setLoading(true);
      const response = await axios.get(
        `https://api.mirai.healthcare/api/attendance/leaves/${userId}`,
        {
          headers: {
            "x-auth": authtoken,
            "Content-Type": "application/json",
          },
        }
      );
      const sortedData = (response.data.leaveData || []).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setGetLeaves({ leaveData: sortedData });
      setFilteredData(sortedData);
      setCachedData(cacheKey, sortedData);
      if (response?.data?.school_time) {
        setSchoolTime(response?.data?.school_time);
      }
    } catch (err) {
      console.error("API error Leaves:", err);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyLeavesData();
  }, []);

  useEffect(() => {
    const fetchHolidayCalendar = async () => {
      if (!sessionId || !classSectionId) return;
      try {
        const response = await axios.get(
          `https://api.mirai.healthcare/api/masters/holidaycalender?session_id=${sessionId}&class_section_id=${classSectionId}`,
          {
            headers: {
              "x-auth": authtoken,
            },
          }
        );
        if (response?.data?.success) {
          setHolidayCalendar(response?.data?.data || []);
        } else {
          setHolidayCalendar([]);
        }
      } catch (error) {
        console.error("Error fetching holiday calendar:", error);
        setHolidayCalendar([]);
      }
    };

    fetchHolidayCalendar();
  }, [sessionId, classSectionId, authtoken]);

  const holidayDateMap = useMemo(() => {
    const map = {};

    holidayCalendar.forEach((holiday) => {
      const start = moment(holiday.start_date).startOf("day");
      const end = moment(holiday.end_date || holiday.start_date).startOf("day");
      if (!start.isValid()) return;

      const lastDate = end.isValid() ? end : start;
      const current = start.clone();
      while (current.isSameOrBefore(lastDate, "day")) {
        const key = current.format("YYYY-MM-DD");
        if (!map[key]) map[key] = [];
        map[key].push({
          title: holiday.title || "Holiday",
          type: holiday.type || "",
          description: holiday.description || "",
        });
        current.add(1, "day");
      }
    });

    return map;
  }, [holidayCalendar]);

  const isSunday = (date) => moment(date).day() === 0;

  const getHolidayListByDate = (date) => {
    const key = moment(date).format("YYYY-MM-DD");
    return holidayDateMap[key] || [];
  };

  const isDateBlocked = (date) => {
    if (!date) return false;
    return isSunday(date) || getHolidayListByDate(date).length > 0;
  };

  const isSelectableDate = (date) => {
    const todayDate = moment().startOf("day");
    return moment(date).isSameOrAfter(todayDate, "day") && !isDateBlocked(date);
  };

  const getDayClassName = (date) => {
    const classes = [];
    if (isSunday(date)) classes.push("leave-day-sunday");
    if (getHolidayListByDate(date).length > 0) classes.push("leave-day-holiday");
    if (isDateBlocked(date)) classes.push("leave-day-blocked");
    return classes.join(" ");
  };

  const getDayTitle = (date) => {
    const info = [];
    if (isSunday(date)) info.push("Sunday");
    const holidays = getHolidayListByDate(date);
    if (holidays.length > 0) {
      info.push(...holidays.map((item) => item.title));
    }
    return info.join(", ");
  };

  const renderDateCell = (day, date) => {
    const tooltip = getDayTitle(date);
    return <span title={tooltip || ""}>{day}</span>;
  };

  const extractDateFromAriaLabel = (ariaLabel) => {
    if (!ariaLabel) return null;
    const cleaned = ariaLabel
      .replace(/^Choose\s+/i, "")
      .replace(/^Not available\s+/i, "")
      .replace(/(\d+)(st|nd|rd|th)/gi, "$1")
      .trim();

    const parsedDate = moment(cleaned, "dddd, MMMM D, YYYY", true);
    return parsedDate.isValid() ? parsedDate : null;
  };

  const applyHolidayTooltipsToCalendar = () => {
    if (!showModal) return;

    const dayNodes = document.querySelectorAll(".react-datepicker__day");
    dayNodes.forEach((node) => {
      const parsedDate = extractDateFromAriaLabel(node.getAttribute("aria-label"));
      if (!parsedDate) return;

      const reasons = [];
      if (parsedDate.day() === 0) reasons.push("Sunday");

      const holidays = holidayDateMap[parsedDate.format("YYYY-MM-DD")] || [];
      if (holidays.length > 0) {
        reasons.push(...holidays.map((item) => item.title));
      }

      if (reasons.length > 0) {
        node.setAttribute("title", [...new Set(reasons)].join(", "));
      } else {
        node.removeAttribute("title");
      }
    });
  };

  const refreshCalendarTooltipTitles = () => {
    setCalendarTooltipRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      applyHolidayTooltipsToCalendar();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [showModal, holidayDateMap, calendarTooltipRefreshKey]);

  // Filter data based on search and status
  useEffect(() => {
    let filtered = getLeaves?.leaveData || [];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.user_remark?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moment(item.start_date).format('DD MMM YYYY').includes(searchTerm)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.leave_status === statusFilter);
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, getLeaves]);

  const handleLeaveCancel = async (leave) => {
    const CancelId = leave?.id;
    if (!CancelId) {
      alert("Invalid leave ID");
      return;
    }

    const startDateStr = moment(leave.start_date).format("YYYY-MM-DD");

    if (isPastDate(startDateStr)) {
      alert("This leave cannot be cancelled as the start date has already passed.");
      return;
    }

    if (!isWithinLeaveBuffer(startDateStr)) {
      Swal.fire({
        icon: "warning",
        title: "Leave",
        text: "Leave cancellation time is over for today.",
      });
      return;
    }

    const confirmCancel = window.confirm(
      "Are you sure you want to cancel this leave?"
    );
    if (!confirmCancel) return;

    try {
      setRejectLoading(true);

      const response = await axios.patch(
        `https://api.mirai.healthcare/api/attendance/leaves/leave-cancle/${CancelId}`,
        {},
        {
          headers: {
            "x-auth": authtoken,
            "Content-Type": "application/json",
          },
        }
      );

      alert(response?.data?.message || "Leave cancelled successfully ✅");
      await fetchMyLeavesData(); // Refresh leave list
    } catch (error) {
      console.error("❌ Cancel leave error:", error);
      alert(error.response?.data?.message || "Failed to cancel leave ❌");
    } finally {
      setRejectLoading(false);
    }
  };

  const getCurrentDate = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const [photo, setPhoto] = useState(null);
  const [Rephoto, setRePhoto] = useState(null);
  const [formData, setFormData] = useState({
    leave_date: getCurrentDate(),
    user_remark: "",
    user_id: userId,
    user_type: "student",
    leave_type: "fullday",
    start_date: "",
    end_date: "",
  });

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const blockedDaysInSelectedRange = useMemo(() => {
    if (!formData.start_date) return [];

    const start = moment(formData.start_date, "YYYY-MM-DD");
    if (!start.isValid()) return [];

    const end = formData.end_date
      ? moment(formData.end_date, "YYYY-MM-DD")
      : start.clone();

    if (!end.isValid()) return [];

    const safeEnd = end.isBefore(start, "day") ? start.clone() : end;
    const current = start.clone();
    const days = [];

    while (current.isSameOrBefore(safeEnd, "day")) {
      const dateKey = current.format("YYYY-MM-DD");
      const dayReasons = [];
      const holidays = holidayDateMap[dateKey] || [];

      if (current.day() === 0) dayReasons.push("Sunday");
      if (holidays.length > 0) {
        dayReasons.push(...holidays.map((h) => h.title));
      }

      if (dayReasons.length > 0) {
        days.push({
          date: dateKey,
          label: current.format("DD MMM YYYY (ddd)"),
          reasons: [...new Set(dayReasons)],
        });
      }
      current.add(1, "day");
    }

    return days;
  }, [formData.start_date, formData.end_date, holidayDateMap]);

  const handleStartDateChange = (date) => {
    setSelectedStartDate(date);
    const formatted = date ? moment(date).format("YYYY-MM-DD") : "";
    handleChange("start_date", formatted);

    if (!date) {
      setSelectedEndDate(null);
      handleChange("end_date", "");
      return;
    }

    if (selectedEndDate && moment(selectedEndDate).isBefore(moment(date), "day")) {
      setSelectedEndDate(null);
      handleChange("end_date", "");
    }
  };

  const handleEndDateChange = (date) => {
    setSelectedEndDate(date);
    handleChange("end_date", date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const [ReformData, setReFormData] = useState({
    leave_date: getCurrentDate(),
    user_remark: "",
    user_id: userId,
    user_type: "student",
    leave_type: "fullday",
    start_date: "",
    end_date: "",
  });

  const handleChangeRe = (key, value) => {
    setReFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    setPhoto(e.target.files[0]);
  };
  const handleReFileChange = (e) => {
    setRePhoto(e.target.files[0]);
  };

  const LEAVE_BUFFER_MINUTES = 30;

  const schoolStartDate = schoolTime
    ? moment(schoolTime.start_date, "DD-MM-YYYY").format("YYYY-MM-DD")
    : null;

  const schoolEndDate = schoolTime
    ? moment(schoolTime.end_date, "DD-MM-YYYY").format("YYYY-MM-DD")
    : null;

  // Earliest selectable/actionable date is always today - no past dates for apply or cancel.
  const isPastDate = (dateStr) =>
    !!dateStr && moment(dateStr, "YYYY-MM-DD").isBefore(moment(), "day");

  // Shared 30-min-before-shift-start buffer, used for both applying and cancelling leave.
  const isWithinLeaveBuffer = (dateStr) => {
    if (!schoolTime || !dateStr) return true;

    const todayStr = moment().format("YYYY-MM-DD");
    if (dateStr !== todayStr) return true;

    if (!moment(todayStr).isBetween(schoolStartDate, schoolEndDate, null, "[]")) {
      return true;
    }

    const shiftStart = moment(schoolTime.shift_start_time, "hh:mm A");
    const lastAllowedTime = shiftStart.clone().subtract(LEAVE_BUFFER_MINUTES, "minutes");

    return moment().isSameOrBefore(lastAllowedTime);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.start_date) {
      alert("Please select a Start Date.");
      return;
    }
    if (!formData.end_date) {
      alert("Please select an End Date.");
      return;
    }

    if (isPastDate(formData.start_date)) {
      alert("Start date cannot be in the past.");
      return;
    }

    if (!isWithinLeaveBuffer(formData.start_date)) {
      Swal.fire({
        icon: "warning",
        title: "Leave",
        text: "Leave apply time is over for today.",
      });
      return;
    }

    const startDateObject = moment(formData.start_date, "YYYY-MM-DD").toDate();
    if (isDateBlocked(startDateObject)) {
      alert("Start date cannot be Sunday or holiday.");
      return;
    }

    if (formData.end_date) {
      const endDateObject = moment(formData.end_date, "YYYY-MM-DD").toDate();
      if (moment(endDateObject).isBefore(moment(startDateObject), "day")) {
        alert("End date cannot be before start date.");
        return;
      }
      if (isDateBlocked(endDateObject)) {
        alert("End date cannot be Sunday or holiday.");
        return;
      }
    }

    setLoading(true);
    const data = new FormData();
    data.append("user_remark", formData.user_remark);
    data.append("user_id", formData.user_id);
    data.append("user_type", formData.user_type);
    data.append("start_date", formData.start_date);
    data.append("end_date", formData.end_date);
    data.append("leave_date", formData.leave_date);
    data.append("leave_type", formData.leave_type);

    if (photo) {
      data.append("leave_img", photo);
    }

    try {
      const response = await axios.post(
        "https://api.mirai.healthcare/api/attendance/leaves",
        data,
        {
          headers: {
            "x-auth": authtoken,
          },
        }
      );

      // alert("Leave request submitted successfully");
      Swal.fire({
            icon: "success",
            title: "Leave",
            text: "Leave request submitted successfully",
          });

      setShowModal(false); // ✅ close modal
      fetchMyLeavesData();
      // Reset form
      setFormData({
        leave_date: getCurrentDate(),
        user_remark: "",
        user_id: userId,
        user_type: "student",
        leave_type: "fullday",
        start_date: "",
        end_date: "",
      });
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setPhoto(null);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to submit leave request.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const leaveData = getLeaves?.leaveData || [];
  const totalLeaves = leaveData.length;
  const pendingLeaves = leaveData.filter((l) => l.leave_status === "pending").length;
  const approvedLeaves = leaveData.filter(
    (l) => l.leave_status === "accepted" || l.leave_status === "approved"
  ).length;
  const ReapplyLeaves = leaveData.filter(l => l.leave_status === 'reapply').length;
  const approvedwithconditionLeaves = leaveData.filter(l => l.leave_status === 'approvedwithcondition').length;
  const cancelledLeaves = leaveData.filter(l => l.leave_status === "cancelled").length;

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "warning", icon: FaClock, label: "Pending" },
      accepted: { variant: "success", icon: FaCheckCircle, label: "Approved" },
      approved: { variant: "success", icon: FaCheckCircle, label: "Approved" },
      rejected: { variant: "danger", icon: FaExclamationTriangle, label: "Rejected" },
      cancelled: { variant: "secondary", icon: FaTimes, label: "Cancelled" },
      reapply: { variant: "info", icon: FaSync, label: "Re-Apply" },
      reapplied: { variant: "primary", icon: FaSync, label: "Re-Applied" },
      approvedwithcondition: {
        variant: "warning",
        icon: FaExclamationTriangle,
        label: "Approved with Condition",
      },
    };

    const config = statusConfig[status] || { variant: 'secondary', icon: FaExclamationTriangle, label: status };
    const IconComponent = config.icon;

    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1 px-2 py-1">
        <IconComponent size={12} />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('DD MMM YYYY') : 'N/A';
  };

  const canCancelLeave = (leave) => {
    if (leave.leave_status !== "pending" && leave.leave_status !== "accepted") {
      return false;
    }

    const startDateStr = moment(leave.start_date).format("YYYY-MM-DD");

    if (isPastDate(startDateStr)) return false;

    return isWithinLeaveBuffer(startDateStr);
  };

  if (loading) {
    return (
      <div className="page-wrapper min-vh-100">
        <Header />
        <Container fluid className="mt-4 d-flex justify-content-center align-items-center min-vh-50">
          <Spinner animation="border" size="lg" />
          {showSlowMsg && (
            <div className="text-center text-muted small mt-3 px-3">
              ⚠️ {message}
            </div>
          )}
        </Container>
      </div>
    );
  }

  const formatDateForInput = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!Rephoto) {
      alert("Please upload document.");
      return;
    }

    setLoading(true);

    const data = new FormData();
    // data.append("leave_id", selectedLeave.id); // 🔴 REQUIRED
    data.append("user_remark", ReformData.user_remark);
    data.append("user_id", ReformData.user_id);
    data.append("user_type", ReformData.user_type);
    data.append("start_date", ReformData.start_date);
    data.append("end_date", ReformData.end_date);
    data.append("leave_date", formData.leave_date);
    data.append("leave_type", ReformData.leave_type);
    data.append("leave_status", 'reapplied');
    if (Rephoto) {
      data.append("leave_img", Rephoto); // 🔴 file key backend ke according
    }

    try {
      await axios.patch(
        `https://api.mirai.healthcare/api/attendance/leaves/${selectedLeave.id}`
,
        data,
        {
          headers: {
            "x-auth": authtoken,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("Leave request updated successfully");
      setShowReApplyModal(false);
      fetchMyLeavesData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update leave request");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <div className="page-wrapper min-vh-100">
      <Header />
      <Container fluid className="mt-4" style={{ paddingLeft: "25px", paddingRight: "25px" }}>
        {/* Stats Row */}
        <div className="row mb-4">
          <div className="col-12">
            <h2
              className="page-title mb-4 mt-4 text-center fw-bold mb-0"
              style={{ letterSpacing: "0.5px" }}>Leaves</h2>

            <div className="leave-stats-scroll mb-3">
              <div className="d-flex gap-3 leave-stats-wrapper">

                <Card className="text-center border-0 shadow-sm stat-card">
                  <Card.Body>
                    <Card.Title className="fs-1 text-black mb-1">{totalLeaves}</Card.Title>
                    <Card.Text className="text-muted mb-0">Total</Card.Text>
                  </Card.Body>
                </Card>

                <Card className="text-center border-0 shadow-sm stat-card">
                  <Card.Body>
                    <Card.Title className="fs-1 text-warning mb-1">{pendingLeaves}</Card.Title>
                    <Card.Text className="text-muted mb-0">Pending</Card.Text>
                  </Card.Body>
                </Card>

                <Card className="text-center border-0 shadow-sm stat-card">
                  <Card.Body>
                    <Card.Title className="fs-1 text-success mb-1">{approvedLeaves}</Card.Title>
                    <Card.Text className="text-muted mb-0">Approved</Card.Text>
                  </Card.Body>
                </Card>

                <Card className="text-center border-0 shadow-sm stat-card">
                  <Card.Body>
                    <Card.Title className="fs-1 text-success mb-1">{approvedwithconditionLeaves}</Card.Title>
                    <Card.Text className="text-muted mb-0">Approved with Condition</Card.Text>
                  </Card.Body>
                </Card>
                <Card className="text-center border-0 shadow-sm stat-card">
                  <Card.Body>
                    <Card.Title className="fs-1 text-primary mb-1">{ReapplyLeaves}</Card.Title>
                    <Card.Text className="text-muted mb-0">Re-Apply</Card.Text>
                  </Card.Body>
                </Card>

                <Card className="text-center border-0 shadow-sm stat-card">
                  <Card.Body>
                    <Card.Title className="fs-1 text-secondary mb-1">{cancelledLeaves}</Card.Title>
                    <Card.Text className="text-muted mb-0">Cancelled</Card.Text>
                  </Card.Body>
                </Card>

              </div>
            </div>

          </div>
        </div>
        <style jsx>{`
.leave-stats-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

.leave-stats-wrapper {
  display: flex;
  flex-wrap: nowrap;
}

.stat-card {
  min-width: 180px;
  flex-shrink: 0;
  border-radius: 15px;
}
.leave-card {
  border-radius: 14px !important;
}

.leave-thumb:hover {
  transform: scale(1.03);
  transition: 0.2s ease;
}

@media (max-width: 576px) {
  .leave-card .d-flex.justify-content-between {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
}

 `}</style>
        {/* Filters and Actions */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <Row className="align-items-center g-3">
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <FaSearch className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by date or remarks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border-start-0"
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <FaFilter className="text-muted" />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="accepted">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="reapply">Re-Apply</option>
                    <option value="reapplied">Re-Applied</option>
                    <option value="approvedwithcondition">Approved with Condition</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={2}>
                <Button variant="primary" onClick={() => setShowModal(true)} className="w-100">
                  <FaPlus className="me-2" />
                  Apply Leave
                </Button>
              </Col>
              <Col md={3}>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" className="flex-fill" onClick={fetchMyLeavesData}>
                    <FaSync className="me-2" />
                    Refresh
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* No Data Screen */}
        {filteredData.length === 0 && (
          <div className="row d-flex justify-content-center align-items-center min-vh-50">
            <Col md={6} className="text-center">

              <h5 className="text-muted mb-3">DATA NOT FOUND!</h5>
              <p className="text-muted mb-4">Kindly check again for latest updates</p>
              <Button variant="primary" onClick={() => setShowModal(true)}>
                <FaPlus className="me-2" />
                Add
              </Button>
            </Col>
          </div>
        )}

        {/* Leave History List */}
        {filteredData.length > 0 && (
          <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="mb-0 fw-bold">Leave History</h5>
              <span className="text-muted small">
                Showing {currentItems.length} of {filteredData.length} leaves
              </span>
            </div>

            {/* Leave Cards */}
            <div className="d-flex flex-column gap-3">
              {currentItems.map((leave, index) => {
                const startIndex = (currentPage - 1) * itemsPerPage;
                const isCancelable = canCancelLeave(leave);

                return (
                  <Card className="border-0 shadow-sm rounded-4 leave-card" key={leave.id}>
                    <Card.Body className="p-3">

                      {/* Top Section */}
                      <div className="d-flex align-items-center gap-3 flex-wrap">

                        {/* Image/Icon */}
                        <div
                          className="leave-thumb d-flex justify-content-center align-items-center"
                          onClick={() => {
                            setSelectedImage(leave.leave_img);
                            setShowImageModal(true);
                          }}
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "12px",
                            overflow: "hidden",
                            cursor: "pointer",
                            background: "#f5f5f5"
                          }}
                        >
                          {leave.leave_img ? (
                            <img
                              src={leave.leave_img}
                              alt="Document"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <FaFileAlt className="text-muted" size={28} />
                          )}
                        </div>

                        {/* Info Area */}
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold d-flex align-items-center gap-2 flex-wrap">
                            <Badge bg="light" text="primary">#{startIndex + index + 1}</Badge>
                            Leave Request
                            {getStatusBadge(leave.leave_status)}
                          </h6>

                          <div className="text-muted small d-flex flex-column gap-1">
                            <div className="d-flex align-items-center gap-1">
                              <FaCalendarAlt className="text-primary" />
                              <span>
                                {formatDate(leave.start_date)}
                                {leave.end_date && ` — ${formatDate(leave.end_date)}`}
                              </span>
                            </div>

                            <div className="d-flex align-items-center gap-1">
                              <FaFileAlt className="text-secondary" />
                              <span>{leave.leave_type || "Full Day Leave"}</span>
                            </div>
                          </div>
                        </div>

                        {/* Right Side Buttons */}
                        <div className="d-flex flex-column gap-2 text-end ms-auto">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="w-100"
                            onClick={() => {
                              setSelectedImage(leave.leave_img);
                              setShowImageModal(true);
                            }}
                          >
                            <FaEye className="me-1" />
                            View
                          </Button>
                          {leave.leave_status === "reapply" && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="w-100"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setReFormData({
                                  start_date: formatDateForInput(leave.start_date),
                                  end_date: formatDateForInput(leave.end_date),
                                  leave_type: leave.leave_type || "fullday",
                                  user_remark: leave.user_remark || "",
                                  leave_date: formatDateForInput(leave.start_date),
                                  user_id: userId,
                                  user_type: "student",
                                });
                                setRePhoto(null);
                                setShowReApplyModal(true);
                              }}

                            >
                              Re-Apply
                            </Button>

                          )}
                          {leave.leave_status === "approvedwithcondition" && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="w-100"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setReFormData({
                                  start_date: formatDateForInput(leave.start_date),
                                  end_date: formatDateForInput(leave.end_date),
                                  leave_type: leave.leave_type || "fullday",
                                  user_remark: leave.user_remark || "",
                                  leave_date: formatDateForInput(leave.start_date),
                                  user_id: userId,
                                  user_type: "student",
                                });
                                setRePhoto(null);
                                setShowReApplyModal(true);
                              }}
                            >
                              Re-Apply
                            </Button>

                          )}

                          {isCancelable && (
                            <Button
                              variant="danger"
                              size="sm"
                              className="w-100"
                              onClick={() => handleLeaveCancel(leave)}
                              disabled={rejectLoading}
                            >
                              {rejectLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FaTimes className="me-1" />
                              )}
                              Cancel
                            </Button>
                          )}
                        </div>

                      </div>

                      {/* Remarks Section */}
                      {(leave.user_remark || leave.admin_remark) && (
                        <div className="mt-3 pt-3 border-top">
                          <small className="fw-semibold">Remarks:</small>
                          <p className="mb-1 mt-1">{leave.user_remark || "No remarks"}</p>

                          {leave.admin_remark && (
                            <p className="text-muted small mb-0">Admin: {leave.admin_remark}</p>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Row className="mt-4">
                <Col>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="text-muted small">
                      Showing {indexOfFirstItem + 1} to{" "}
                      {Math.min(indexOfLastItem, filteredData.length)} of{" "}
                      {filteredData.length} entries
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>

                      <span className="align-self-center small fw-bold">
                        {currentPage} / {totalPages}
                      </span>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}


        {/* Apply Leave Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="md">
          <Modal.Header closeButton>
            <Modal.Title>Apply Leave</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="fromDate" className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <DatePicker
                  selected={selectedStartDate}
                  onChange={handleStartDateChange}
                  onCalendarOpen={refreshCalendarTooltipTitles}
                  onMonthChange={refreshCalendarTooltipTitles}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  minDate={new Date()}
                  filterDate={isSelectableDate}
                  dayClassName={getDayClassName}
                  renderDayContents={renderDateCell}
                  title={selectedStartDate ? getDayTitle(selectedStartDate) : ""}
                  placeholderText="Select Start Date"
                  required
                />
              </Form.Group>

              <Form.Group controlId="toDate" className="mb-3">
                <Form.Label>End Date</Form.Label>
                <DatePicker
                  selected={selectedEndDate}
                  onChange={handleEndDateChange}
                  onCalendarOpen={refreshCalendarTooltipTitles}
                  onMonthChange={refreshCalendarTooltipTitles}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  minDate={selectedStartDate || new Date()}
                  filterDate={isSelectableDate}
                  dayClassName={getDayClassName}
                  renderDayContents={renderDateCell}
                  title={selectedEndDate ? getDayTitle(selectedEndDate) : ""}
                  placeholderText="Select End Date"
                />
              </Form.Group>

              <Alert variant="light" className="small py-2">
                <span className="fw-semibold">Note:</span> Sundays and holiday dates are highlighted and cannot be selected.
              </Alert>

              {blockedDaysInSelectedRange.length > 0 && (
                <Alert variant="warning" className="small py-2">
                  <div className="fw-semibold mb-1">
                    Holiday/Sunday found between selected Start and End date:
                  </div>
                  {blockedDaysInSelectedRange.map((item) => (
                    <div key={item.date}>
                      {item.label}: {item.reasons.join(", ")}
                    </div>
                  ))}
                </Alert>
              )}

              <Form.Group controlId="leaveType" className="mb-3">
                <Form.Label>Leave Type</Form.Label>
                <Form.Select
                  value={formData.leave_type}
                  onChange={(e) => handleChange("leave_type", e.target.value)}
                  required
                >
                  <option value="fullday">Full Day Leave</option>
                  <option value="halfday">Half Day Leave</option>
                  <option value="medical">Medical Leave</option>
                  {/* <option value="casual">Casual Leave</option> */}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.user_remark}
                  onChange={(e) => handleChange("user_remark", e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Upload Document</Form.Label>
                <Form.Control type="file" onChange={handleFileChange} />
              </Form.Group>

              <Modal.Footer className="d-flex justify-content-between">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading || !formData.end_date}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal.Body>
        </Modal>
        {/* Re-Apply Leave Modal */}
        <Modal show={showReApplyModal} onHide={() => setShowReApplyModal(false)} centered size="md">
          <Modal.Header closeButton>
            <Modal.Title>Re-Apply Leave</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleUpdate}>
              <Form.Group controlId="fromDate" className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={ReformData?.start_date}
                  disabled
                />
              </Form.Group>

              <Form.Group controlId="toDate" className="mb-3">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={ReformData?.end_date}
                  disabled
                />
              </Form.Group>

              <Form.Group controlId="leaveType" className="mb-3">
                <Form.Label>Leave Type</Form.Label>
                <Form.Select
                  value={ReformData.leave_type}
                  onChange={(e) => handleChangeRe("leave_type", e.target.value)}
                  required
                >
                  <option value="fullday">Full Day Leave</option>
                  <option value="halfday">Half Day Leave</option>
                  <option value="medical">Medical Leave</option>
                  {/* <option value="casual">Casual Leave</option> */}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={ReformData.user_remark}
                  onChange={(e) => handleChangeRe("user_remark", e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Upload Document</Form.Label>
                <Form.Control type="file" onChange={handleReFileChange} />
              </Form.Group>

              <Modal.Footer className="d-flex justify-content-between">
                <Button variant="secondary" onClick={() => setShowReApplyModal(false)}>
                  Close
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : "Update"}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Image Modal */}
        <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>View Document</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {selectedImage && <img src={selectedImage} alt="Document" style={{ width: "100%", height: "auto", maxHeight: "70vh" }} />}
          </Modal.Body>
        </Modal>
      </Container>
      <style>{`
        .react-datepicker__day.leave-day-blocked {
          background: #fce8e8;
          color: #a94442;
          border-radius: 999px;
          font-weight: 600;
        }

        .react-datepicker__day.leave-day-sunday {
          color: #d9534f;
        }

        .react-datepicker__day.leave-day-holiday {
          border: 1px solid #f0ad4e;
        }
      `}</style>
    </div>
  );
};

export default StudentLeave;
